/**
 * The note page's "Live draft" (#14479, part of #14371).
 *
 * While a note is still processing after Stop, the person who recorded it is
 * shown the last live summary their recording wrote — the one the recording
 * bar's panel was showing (#14379) — until the final summary replaces it.
 *
 * The read is the owner-only `getLiveSummary` (`@/app/actions/live-summary`),
 * scoped to `{ id, orgId, createdById: actorId }`: another viewer of the
 * note, an admin viewing as the recorder, and a recorder with
 * `ai_note_processing` off (#16725) all get its not-found, which this module turns into "no draft". So
 * the page needs no gate of its own; this is only WHEN a readable answer is
 * a draft worth showing:
 *
 *   - processing is active (`isNoteProcessingActive`) — once the stage is
 *     `DONE` or `PASS_FAILED` the draft is gone;
 *   - the final summary does not exist yet — the draft is never shown beside
 *     (or after) the summary it stands in for;
 *   - a snapshot was written and has topics — `waiting` and `error` are the
 *     live panel's states, not a draft.
 *
 * The recording asked about is the note's LATEST, the one the processing
 * card reports on. A note recorded in parts (#14427) therefore reads the
 * newest part — but only until the note has a summary: summaries are per
 * note, so once an earlier part's summary exists, a later part shows that
 * summary rather than a draft beside it.
 */

import type { GetLiveSummaryResult } from '@/app/actions/live-summary'
import type { LiveTopic } from '@/lib/notetaker/live-summary-fold'
import type { LiveTranscriptView } from '@/lib/notetaker/live-transcript-view'

import {
  isNoteProcessingActive,
  type NoteProcessingStage,
} from './note-processing'

/** The section's label — the recording bar panel's title, the same words. */
export const NOTE_LIVE_DRAFT_LABEL = 'Live draft'

/** The one line under it: what it is, and that it will be replaced. */
export const NOTE_LIVE_DRAFT_EXPLAINER =
  'From your recording, up to when you stopped. The final summary is being written from the full transcript and will replace it.'

/** The same line while the recording is still running. */
export const NOTE_LIVE_DRAFT_RECORDING_EXPLAINER =
  'From your recording so far. When you stop, the final summary is written from the full transcript and replaces it.'

/** What the note page renders as the draft. Plain data: it crosses to the client. */
export interface NoteLiveDraft {
  recordingId: string
  version: number | null
  topics: LiveTopic[]
  /** The finalized tail and the cited spans each line opens into. */
  transcript: LiveTranscriptView
}

interface NoteLiveDraftContext {
  stage: NoteProcessingStage
  /** The note has its final summary. */
  hasSummary: boolean
  /** The note's latest recording, or null for a notes-only note. */
  recordingId: string | null
}

/** Whether the page should ask `getLiveSummary` at all. */
export function shouldReadNoteLiveDraft(input: NoteLiveDraftContext): boolean {
  return (
    input.recordingId !== null &&
    !input.hasSummary &&
    isNoteProcessingActive(input.stage)
  )
}

/** The draft to show, or null. See the module header. Pure. */
export function noteLiveDraftFor(
  input: NoteLiveDraftContext & { result: GetLiveSummaryResult },
): NoteLiveDraft | null {
  if (!shouldReadNoteLiveDraft(input) || input.recordingId === null) {
    return null
  }
  const { result } = input
  if (!result.success || result.state !== 'ready') return null
  if (result.topics.length === 0) return null
  return {
    recordingId: input.recordingId,
    version: result.version,
    topics: result.topics,
    transcript: result.transcript,
  }
}
